/*
 * Katana hero — full-screen media carousel.
 * Plays the current slide to completion (video: on ended; image: after a
 * configurable duration), then slides right to the next. Loops.
 *
 * Nothing downloads until it's needed: the markup ships preload="none" with the
 * real URL in data-src (see katana-hero-media.liquid), and only the first
 * slide's poster as a poster attribute — the rest sit in data-poster. The first
 * slide's footage is fetched once the browser is idle, and each following slide's
 * still and footage are fetched while the one before it plays, so the page paints
 * on a single image instead of megabytes of video.
 *
 * The slide's CTA is faded in and out rather than sitting on screen for the whole
 * clip: it comes up once the footage is under way and goes down timed to finish
 * as the clip ends, so it is never caught hanging over a crossfade.
 */
(function () {
  'use strict';

  var mqReduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var mqReduceData = window.matchMedia && window.matchMedia('(prefers-reduced-data: reduce)');
  // Data Saver and 2G/3G connections get the posters and nothing else — a hero
  // clip is several megabytes, which on those connections is the whole page.
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var thinPipe = !!conn && (conn.saveData === true || /^(slow-)?2g$/.test(conn.effectiveType || '') || conn.effectiveType === '3g');
  // With either preference set the posters are the whole show.
  var posterOnly =
    (mqReduceMotion && mqReduceMotion.matches) || (mqReduceData && mqReduceData.matches) || thinPipe;

  function whenIdle(fn, timeout) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(fn, { timeout: timeout || 2000 });
    } else {
      setTimeout(fn, 400);
    }
  }

  function initHero(root) {
    var track = root.querySelector('[data-khero-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-khero-slide]'));
    if (!track || slides.length === 0) return;

    var index = 0;
    var timer = null;
    var single = slides.length === 1;

    function videoOf(slide) {
      // A slide may hold both a desktop and a mobile video (only one is shown
      // per viewport). Return the visible one so playback matches what's on
      // screen; fall back to the first if none report as visible.
      var vids = slide.querySelectorAll('video');
      for (var i = 0; i < vids.length; i++) {
        if (vids[i].offsetParent !== null) return vids[i];
      }
      return vids[0] || null;
    }

    // Slides after the first hold their still in data-poster so it doesn't
    // compete with the one being painted. Attached separately from the footage:
    // in poster-only mode the stills are the whole carousel.
    function attachPoster(video) {
      if (!video) return;
      var url = video.getAttribute('data-poster');
      if (url && !video.getAttribute('poster')) {
        video.setAttribute('poster', url);
        video.removeAttribute('data-poster');
      }
    }

    // Attach the real source the first time a video is actually wanted. Only
    // ever the visible one, so a phone never downloads the desktop cut.
    function load(video) {
      if (!video || posterOnly) return false;
      var src = video.getAttribute('data-src');
      if (src && !video.getAttribute('src')) {
        video.setAttribute('src', src);
        video.load();
      }
      return !!video.getAttribute('src');
    }

    function loadSlide(n) {
      if (!slides.length) return;
      var slide = slides[(n + slides.length) % slides.length];
      var video = videoOf(slide);
      attachPoster(video);
      load(video);
    }

    function clearTimer() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    // --- CTA choreography ---------------------------------------------------
    // Long enough that the button arrives after the eye has taken in the frame,
    // short enough that it is up well before a short clip is over.
    var BTN_DELAY = 700;
    var btnTimers = [];

    function fadeMs(el) {
      // Duration lives in CSS (--khero-btn-fade) so the transition and the
      // schedule can't disagree about how long a fade takes.
      var raw = window.getComputedStyle(el).getPropertyValue('--khero-btn-fade').trim();
      var n = parseFloat(raw);
      if (!isFinite(n) || n <= 0) return 900;
      // A custom property comes back however the browser chose to serialise it,
      // and 900ms arrives as ".9s" — so read the scale off the unit rather than
      // trusting the number. Getting this wrong is quiet: the fade-out would
      // start where it should have finished, i.e. over the crossfade.
      return /ms$/.test(raw) ? n : n * 1000;
    }

    function clearBtnTimers() {
      for (var i = 0; i < btnTimers.length; i++) clearTimeout(btnTimers[i]);
      btnTimers = [];
    }

    function hideButtons() {
      var btns = root.querySelectorAll('[data-khero-btn]');
      for (var i = 0; i < btns.length; i++) btns[i].classList.remove('is-in');
    }

    // seconds = how long this slide holds the screen. The fade-out is scheduled
    // to *finish* on that mark, so it plays out over the tail of the clip
    // instead of over the crossfade into the next one.
    function cueButton(slide, seconds) {
      var btn = slide.querySelector('[data-khero-btn]');
      if (!btn) return;
      var fade = fadeMs(btn);
      btnTimers.push(setTimeout(function () {
        btn.classList.add('is-in');
      }, BTN_DELAY));
      if (single) return; // one slide loops forever: nothing to fade out for
      // On a clip too short to fit the full in-hold-out, keep it up for a beat
      // rather than cross-fading it against itself.
      var out = Math.max(BTN_DELAY + fade * 2, seconds * 1000 - fade);
      btnTimers.push(setTimeout(function () {
        btn.classList.remove('is-in');
      }, out));
    }

    function slideSeconds(slide, video) {
      if (video && isFinite(video.duration) && video.duration > 0) return video.duration;
      return parseFloat(slide.getAttribute('data-duration')) || 6;
    }

    function paint() {
      // Crossfade: only the current slide carries .is-active (opacity 1).
      for (var i = 0; i < slides.length; i++) {
        slides[i].classList.toggle('is-active', i === index);
      }
    }

    function next() {
      go(index + 1);
    }

    function go(n) {
      clearTimer();
      clearBtnTimers();
      // Covers the case where a clip ended sooner than its metadata promised:
      // the button still leaves, just against the crossfade.
      hideButtons();
      var prevVideo = videoOf(slides[index]);
      if (prevVideo) {
        prevVideo.pause();
      }
      index = (n + slides.length) % slides.length;
      paint();
      play();
    }

    function play() {
      var slide = slides[index];
      var video = videoOf(slide);
      // Whatever happens next, the slide on screen needs its still.
      attachPoster(video);

      if (video && load(video)) {
        video.muted = true;
        video.loop = single;
        try { video.currentTime = 0; } catch (e) {}
        var p = video.play();
        if (p && p.catch) {
          p.catch(function () {
            // Autoplay blocked: advance on a timer instead so the carousel
            // never stalls.
            if (!single) fallbackTimer(video);
          });
        }
        cueButton(slide, slideSeconds(slide, video));
        // Before metadata lands the only length available is the block's
        // configured fallback, which is rarely the clip's real one. Re-cue
        // against the truth as soon as it arrives.
        if (!(isFinite(video.duration) && video.duration > 0)) {
          video.addEventListener('loadedmetadata', function onMeta() {
            video.removeEventListener('loadedmetadata', onMeta);
            if (slides[index] !== slide) return; // carousel already moved on
            clearBtnTimers();
            cueButton(slide, slideSeconds(slide, video));
          });
        }
        if (!single) {
          video.onended = next;
          fallbackTimer(video); // safety if 'ended' never fires
          // Fetch the next slide while this one plays, so the crossfade lands
          // on footage rather than a poster.
          whenIdle(function () { loadSlide(index + 1); }, 4000);
        }
      } else {
        // No video, or we're deliberately showing posters only: hold the slide
        // for its configured duration.
        var dur = parseFloat(slide.getAttribute('data-duration')) || 6;
        cueButton(slide, dur);
        if (single) return;
        timer = setTimeout(next, dur * 1000);
        // load() is a no-op here, but the next slide's still still has to be in
        // place before the crossfade reaches it.
        whenIdle(function () { loadSlide(index + 1); }, 4000);
      }
    }

    function fallbackTimer(video) {
      clearTimer();
      var dur = video && isFinite(video.duration) && video.duration > 0 ? video.duration + 0.5 : 12;
      timer = setTimeout(next, dur * 1000);
    }

    paint();
    // Let the poster paint (and the rest of the page settle) before asking the
    // network for video.
    whenIdle(play);
  }

  function boot() {
    var heroes = document.querySelectorAll('[data-khero]');
    Array.prototype.forEach.call(heroes, initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
